import React, { useState, Fragment, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import cn from 'classnames';

import Icon from '../../../../Icon';
import DeletePopup from '../DeletePopup';
import PopoverMenu from '../../../../common/PopoverMenu';
import SubscriptionInfoPopup from '../SubscriptionInfoPopup';
import PaymentMethodLocationsPopup from '../PaymentMethodLocationsPopup';

import { COLORS } from '../../../../../constants/styles';
import { UNKNOWN_VAULT_ACCOUNT_TYPE_TITLE } from '../../../../../constants/payments/vault-account-types';

import useIsLoggedInUser from '../../../../../hooks/useIsLoggedInUser';

import styles from './styles.module.styl';

const POPOVER_STYLES = {
  marginTop: '10px',
  marginLeft: '10px',
};

const Card = ({ card }) => {
  const { formatMessage } = useIntl();

  const [anchorEl, setAnchorEl] = useState(null);

  const [selectedItemId, setSelectedItemId] = useState(null);

  const [
    paymentMethodLocationsPopupOpened,
    setPaymentMethodLocationsPopupOpened,
  ] = useState(false);

  const [
    subscriptionInfoPopupOpened,
    setSubscriptionInfoPopupOpened,
  ] = useState(false);

  const isLoggedInUser = useIsLoggedInUser();

  const handlePopoverOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const isUnknownAccountType =
    card.accountType === UNKNOWN_VAULT_ACCOUNT_TYPE_TITLE;

  const handleClose = () => {
    handlePopoverClose();
    setSelectedItemId(null);
  };

  const handleItemClick = id => {
    handlePopoverClose();
    setSelectedItemId(id);
  };

  const togglePaymentMethodLocationsPopup = useCallback(() => {
    handlePopoverClose();
    setPaymentMethodLocationsPopupOpened(prev => !prev);
  }, []);

  const toggleSubscriptionInfoPopup = useCallback(() => {
    handlePopoverClose();
    setSubscriptionInfoPopupOpened(prev => !prev);
  }, []);

  const menuItems = useMemo(() => {
    const seeAssociatedLocationsTitle = formatMessage({
      id: 'profile.payment.cardMenu.seeAssociatedLocations',
    });

    const removePaymentMethodTitle = formatMessage({
      id: 'profile.payment.cardMenu.removePaymentMethod',
    });

    return [
      {
        text: (
          <div className={styles.menuItem}>
            <Icon
              name="location"
              style={{
                fill: COLORS.bluePrimary,
              }}
            />
            {seeAssociatedLocationsTitle}
          </div>
        ),
        onClick: togglePaymentMethodLocationsPopup,
      },
      {
        text: (
          <div className={styles.menuItem}>
            <Icon
              name={card.hasActiveSubscriptions ? 'info' : 'remove'}
              style={{
                fill: card.hasActiveSubscriptions
                  ? COLORS.orange
                  : COLORS.bluePrimary,
                stroke: card.hasActiveSubscriptions && COLORS.orange,
              }}
            />
            {removePaymentMethodTitle}
          </div>
        ),
        onClick: () => {
          if (!card.hasActiveSubscriptions) {
            handleItemClick(card.id);
          } else {
            toggleSubscriptionInfoPopup();
          }
        },
        borderTop: true,
      },
    ];
  }, [
    card,
    formatMessage,
    handleItemClick,
    togglePaymentMethodLocationsPopup,
    toggleSubscriptionInfoPopup,
  ]);

  return (
    <Fragment>
      <div
        className={cn(styles.container, {
          [styles.containerError]: card.isExpired,
        })}
      >
        <div className={styles.row}>
          <div className={styles.cardNumber}>{card.label}</div>
          {isLoggedInUser && (
            <div className={styles.icon}>
              <Icon
                name="three-dots-16"
                size={16}
                onClick={handlePopoverOpen}
              />
              <PopoverMenu
                isOpen={open}
                anchorEl={anchorEl}
                items={menuItems}
                onClose={handlePopoverClose}
                size="large"
                style={POPOVER_STYLES}
              />
            </div>
          )}
        </div>

        <div
          className={cn(styles.expDate, {
            [styles.expDateExpired]: card.isExpired,
          })}
        >{`${formatMessage({
          id: 'common.expDate',
        })}: ${card.expDate}`}</div>
        <div
          className={cn(styles.accountType, {
            [styles.unknownAccountType]: isUnknownAccountType,
          })}
        >
          {card.accountType}
        </div>

        {card.isExpired && (
          <div className={styles.expired}>
            {formatMessage({ id: 'common.expired' })}
          </div>
        )}

        <DeletePopup
          itemId={selectedItemId}
          opened={Boolean(selectedItemId)}
          onClose={handleClose}
        />
      </div>

      {paymentMethodLocationsPopupOpened && (
        <PaymentMethodLocationsPopup
          cardId={card.id}
          onClose={togglePaymentMethodLocationsPopup}
          opened={paymentMethodLocationsPopupOpened}
        />
      )}

      {subscriptionInfoPopupOpened && (
        <SubscriptionInfoPopup
          opened={subscriptionInfoPopupOpened}
          onClose={toggleSubscriptionInfoPopup}
        />
      )}
    </Fragment>
  );
};

export default Card;

Card.propTypes = {
  card: PropTypes.object.isRequired,
};



// WEBPACK FOOTER //
// ./src/components/ProfileLayout/ProfilePaymentMethods/Payments/Card/index.jsx